import React, {useEffect, useRef} from 'react';
import {IconSliders, IconSort} from '../UI/NavIcons';

/** Общие варианты сортировки для каталога и страницы категории. */
export const SHOP_SORT_OPTIONS = [
    {value: 'id,desc', label: 'Сначала новые'},
    {value: 'title,asc', label: 'Название А→Я'},
    {value: 'title,desc', label: 'Название Я→А'},
    {value: 'price,asc', label: 'Сначала дешевле'},
    {value: 'price,desc', label: 'Сначала дороже'},
];

function FilterToolbarPopover({
    popoverRef,
    idPrefix,
    sort,
    setSort,
    sortOptions,
    filtersOpen,
    setFiltersOpen,
    sortOpen,
    setSortOpen,
    openSort,
    openFilters,
    query,
    setQuery,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    inStockOnly,
    setInStockOnly,
}) {
    const filtersPanelId = `${idPrefix}-filters`;
    const sortPanelId = `${idPrefix}-sort`;

    return (
        <div ref={popoverRef} className="relative">
            <div className="flex items-center gap-1.5">
                <button
                    type="button"
                    onClick={openSort}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-stone-200/90 bg-white text-bark-600 shadow-sm touch-manipulation hover:bg-cream-100 active:bg-cream-200"
                    aria-expanded={sortOpen}
                    aria-controls={sortPanelId}
                    title={sortOpen ? 'Закрыть сортировку' : 'Сортировка'}
                >
                    <IconSort className="h-4 w-4"/>
                </button>
                <button
                    type="button"
                    onClick={openFilters}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-stone-200/90 bg-white text-bark-600 shadow-sm touch-manipulation hover:bg-cream-100 active:bg-cream-200"
                    aria-expanded={filtersOpen}
                    aria-controls={filtersPanelId}
                    title={filtersOpen ? 'Скрыть фильтры' : 'Фильтры'}
                >
                    <IconSliders className="h-4 w-4"/>
                </button>
            </div>

            {sortOpen && (
                <div
                    id={sortPanelId}
                    className="absolute right-0 z-30 mt-2 max-h-[min(70vh,24rem)] w-[min(calc(100vw-2rem),20rem)] overflow-y-auto rounded-2xl border border-stone-200/80 bg-white p-4 shadow-lg sm:w-80"
                    role="dialog"
                    aria-label="Сортировка"
                >
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-bark-500">Сортировка</p>
                    <div className="grid gap-1">
                        {sortOptions.map((o) => (
                            <label
                                key={o.value}
                                className="flex cursor-pointer items-center gap-2 rounded-xl px-2 py-2 text-sm text-bark-800 hover:bg-cream-100 touch-manipulation"
                            >
                                <input
                                    type="radio"
                                    name={`${idPrefix}-sort`}
                                    value={o.value}
                                    checked={sort === o.value}
                                    onChange={() => setSort(o.value)}
                                    className="h-4 w-4 shrink-0 border-stone-300 text-pine-700"
                                />
                                {o.label}
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {filtersOpen && (
                <div
                    id={filtersPanelId}
                    className="absolute right-0 z-30 mt-2 max-h-[min(70vh,28rem)] w-[min(calc(100vw-2rem),20rem)] overflow-y-auto rounded-2xl border border-stone-200/80 bg-white p-4 shadow-lg sm:w-80"
                >
                    <div className="grid gap-3">
                        <div>
                            <label className="lut-label" htmlFor={`${idPrefix}-q`}>
                                Поиск по названию
                            </label>
                            <input
                                id={`${idPrefix}-q`}
                                className="lut-input min-h-10 text-sm touch-manipulation"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Например, ваниль"
                                autoComplete="off"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="lut-label" htmlFor={`${idPrefix}-min`}>
                                    Цена от
                                </label>
                                <input
                                    id={`${idPrefix}-min`}
                                    className="lut-input min-h-10 text-sm touch-manipulation"
                                    inputMode="numeric"
                                    value={minPrice}
                                    onChange={(e) => setMinPrice(e.target.value.replace(/\D/g, ''))}
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="lut-label" htmlFor={`${idPrefix}-max`}>
                                    Цена до
                                </label>
                                <input
                                    id={`${idPrefix}-max`}
                                    className="lut-input min-h-10 text-sm touch-manipulation"
                                    inputMode="numeric"
                                    value={maxPrice}
                                    onChange={(e) => setMaxPrice(e.target.value.replace(/\D/g, ''))}
                                    placeholder="—"
                                />
                            </div>
                        </div>
                        <label className="flex cursor-pointer items-center gap-2 text-xs text-bark-700 touch-manipulation">
                            <input
                                type="checkbox"
                                className="h-4 w-4 shrink-0 rounded border-stone-300 text-pine-700"
                                checked={inStockOnly}
                                onChange={(e) => setInStockOnly(e.target.checked)}
                            />
                            Только в наличии
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
}

export function ShopFiltersSection({
    leading,
    sort,
    setSort,
    sortOptions = SHOP_SORT_OPTIONS,
    filtersOpen,
    setFiltersOpen,
    sortOpen,
    setSortOpen,
    query,
    setQuery,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    inStockOnly,
    setInStockOnly,
    idPrefix = 'shop',
}) {
    /** Только кнопки и выпадашки — клик по центральному заголовку и выше считается «мимо». */
    const popoverRef = useRef(null);

    useEffect(() => {
        if (!filtersOpen && !sortOpen) {
            return undefined;
        }
        const targetEl = (e) => {
            const n = e.target;
            if (n == null) return null;
            return n.nodeType === Node.TEXT_NODE ? n.parentElement : n;
        };
        const close = (e) => {
            const t = targetEl(e);
            if (!t || !popoverRef.current || popoverRef.current.contains(t)) {
                return;
            }
            setFiltersOpen(false);
            setSortOpen(false);
        };
        const touchOpts = {capture: true, passive: true};
        document.addEventListener('pointerdown', close, true);
        document.addEventListener('mousedown', close, true);
        document.addEventListener('touchstart', close, touchOpts);
        return () => {
            document.removeEventListener('pointerdown', close, true);
            document.removeEventListener('mousedown', close, true);
            document.removeEventListener('touchstart', close, touchOpts);
        };
    }, [filtersOpen, sortOpen, setFiltersOpen, setSortOpen]);

    useEffect(() => {
        if (!filtersOpen && !sortOpen) {
            return undefined;
        }
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setFiltersOpen(false);
                setSortOpen(false);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [filtersOpen, sortOpen, setFiltersOpen, setSortOpen]);

    const openFilters = () => {
        setSortOpen(false);
        setFiltersOpen((o) => !o);
    };

    const openSort = () => {
        setFiltersOpen(false);
        setSortOpen((o) => !o);
    };

    const hasLeading = leading != null && leading !== false;

    const popoverProps = {
        popoverRef,
        idPrefix,
        sort,
        setSort,
        sortOptions,
        filtersOpen,
        setFiltersOpen,
        sortOpen,
        setSortOpen,
        openSort,
        openFilters,
        query,
        setQuery,
        minPrice,
        setMinPrice,
        maxPrice,
        setMaxPrice,
        inStockOnly,
        setInStockOnly,
    };

    return (
        <div className="relative mb-6">
            {hasLeading ? (
                <div className="grid min-h-[2.25rem] grid-cols-[1fr_auto_1fr] items-center gap-2 sm:min-h-10 sm:gap-3">
                    <div className="min-w-0" aria-hidden="true"/>
                    <div className="min-w-0 max-w-[min(100vw-8rem,28rem)] justify-self-center px-2 text-center">
                        {leading}
                    </div>
                    <div className="flex min-w-0 justify-end">
                        <FilterToolbarPopover {...popoverProps} />
                    </div>
                </div>
            ) : (
                <div className="flex min-h-[2.25rem] items-center justify-end sm:min-h-10">
                    <FilterToolbarPopover {...popoverProps} />
                </div>
            )}
        </div>
    );
}
